"use client";

import { useI18n } from "@/lib/i18n";

function SectionPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg overflow-hidden border" style={{ borderColor: "var(--card-border)", boxShadow: "var(--card-shadow)" }}>
      <div className="px-4 py-3" style={{ background: "var(--section-header-bg)" }}>
        <h3 className="text-sm font-semibold" style={{ color: "var(--section-header-text)", fontFamily: "var(--font-brand), serif" }}>{title}</h3>
      </div>
      <div className="bg-white p-5">{children}</div>
    </div>
  );
}

function FieldRow({ label, placeholder }: { label: string; placeholder: string }) {
  return (
    <div className="py-3 border-b last:border-0" style={{ borderColor: "var(--card-border)" }}>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="h-9 rounded-lg border px-3 flex items-center text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text-muted)" }}>
        {placeholder}
      </div>
    </div>
  );
}

function ToggleRow({ label, description }: { label: string; description: string }) {
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: "var(--card-border)" }}>
      <div>
        <p className="text-sm font-semibold" style={{ color: "var(--text-heading)" }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{description}</p>
      </div>
      <div className="w-10 h-6 rounded-full shrink-0 relative" style={{ background: "#e5e7eb" }}>
        <div className="absolute start-1 top-1 w-4 h-4 rounded-full bg-white shadow-sm" />
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const { t } = useI18n();

  const integrationItems = [
    t("API key", "مفتاح API"),
    t("SSO provider", "مزود SSO"),
    t("Webhook URL", "رابط Webhook"),
    t("Data source", "مصدر البيانات"),
  ];

  return (
    <div className="space-y-6" style={{ padding: "var(--page-padding)" }}>
      {/* ── Page header ── */}
      <div>
        <h1 className="page-title">{t("Settings", "الإعدادات")}</h1>
        <p className="page-subtitle">{t("Application and account configuration", "إعدادات التطبيق والحساب")}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Profile ── */}
        <SectionPanel title={t("Profile", "الملف الشخصي")}>
          <FieldRow label={t("Full name", "الاسم الكامل")} placeholder={t("Not set", "غير محدد")} />
          <FieldRow label={t("Email address", "البريد الإلكتروني")} placeholder={t("Not set", "غير محدد")} />
          <FieldRow label={t("Role", "الدور")} placeholder={t("Not set", "غير محدد")} />
          <FieldRow label={t("Department", "القسم")} placeholder={t("Not set", "غير محدد")} />
          <div className="mt-4 flex justify-end">
            <button type="button" className="px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: "var(--primary-btn)" }}>{t("Save changes", "حفظ التغييرات")}</button>
          </div>
        </SectionPanel>

        {/* ── Notifications ── */}
        <SectionPanel title={t("Notifications", "الإشعارات")}>
          <ToggleRow label={t("Email notifications", "إشعارات البريد")} description={t("Receive audit alerts by email", "استلام تنبيهات التدقيق عبر البريد")} />
          <ToggleRow label={t("System alerts", "تنبيهات النظام")} description={t("In-app notifications for critical gaps", "إشعارات داخل التطبيق للفجوات الحرجة")} />
          <ToggleRow label={t("Weekly digest", "الملخص الأسبوعي")} description={t("Summary report every Monday", "تقرير ملخص كل يوم إثنين")} />
          <ToggleRow label={t("Deadline reminders", "تذكيرات المواعيد")} description={t("Remind 7 days before target deadline", "تذكير قبل 7 أيام من الموعد المحدد")} />
        </SectionPanel>

        {/* ── Security ── */}
        <SectionPanel title={t("Security", "الأمان")}>
          <FieldRow label={t("Current password", "كلمة المرور الحالية")} placeholder="••••••••" />
          <FieldRow label={t("New password", "كلمة المرور الجديدة")} placeholder="••••••••" />
          <FieldRow label={t("Confirm password", "تأكيد كلمة المرور")} placeholder="••••••••" />
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded border" style={{ borderColor: "var(--card-border)" }} />
              <span className="text-sm" style={{ color: "var(--text-body)" }}>{t("Enable 2FA", "تفعيل المصادقة الثنائية")}</span>
            </div>
            <button type="button" className="px-4 py-2 rounded-lg text-sm font-bold text-white" style={{ background: "var(--primary-btn)" }}>{t("Update", "تحديث")}</button>
          </div>
        </SectionPanel>

        {/* ── Integrations ── */}
        <SectionPanel title={t("Integrations", "التكاملات")}>
          {integrationItems.map((item) => (
            <div key={item} className="flex items-center justify-between py-3 border-b last:border-0" style={{ borderColor: "var(--card-border)" }}>
              <span className="text-sm" style={{ color: "var(--text-heading)" }}>{item}</span>
              <span className="text-xs px-2 py-1 rounded" style={{ background: "#f0f4fa", color: "var(--text-muted)" }}>{t("Not configured", "غير مهيأ")}</span>
            </div>
          ))}
          <p className="mt-3 text-xs" style={{ color: "var(--text-muted)" }}>{t("Configure your integrations above to get started.", "قم بتهيئة التكاملات أعلاه للبدء.")}</p>
        </SectionPanel>
      </div>
    </div>
  );
}
